import React, { useState, useEffect } from 'react'
import { Modal, Form } from 'semantic-ui-react'
import {
    AppPrimaryButton,
    AppSecondaryButton,
    FormTextFormField,
    FormTextAreaFormField,
    FormDropdownField,
    FormDateTimeField,
} from 'elements'
import { AnnouncementService, UsersService } from 'utility/services'
import { X } from 'phosphor-react'
import { useAuth } from 'context'
import { GetAuditCompanyList } from 'hooks'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

const schema = yup.object().shape({
    title: yup.string().required('Title is required'),
    body: yup.string().required('Body is required'),
    priority: yup.number().default(1),
    targetScope: yup.number().default(0),
    targetCompanyId: yup.mixed().when('targetScope', {
        is: val => val === 1 || val === 2,
        then: yup.mixed().required('Company is required'),
    }),
    targetUserId: yup.mixed().when('targetScope', {
        is: 2,
        then: yup.mixed().required('User is required'),
    }),
    expire: yup.date().nullable(),
})

export const AnnouncementModal = ({ open, onClose, announcement }) => {
    const { user: currentUser } = useAuth()
    const [loading, setLoading] = useState(false)
    const [userOptions, setUserOptions] = useState([])
    const [searchQuery, setSearchQuery] = useState('')
    const isSuperAdmin = currentUser?.IsSuperAdmin
    const { options: companyOptions, filterChanged } = GetAuditCompanyList(isSuperAdmin, true, 100)

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            filterChanged({ search: searchQuery })
        }, 300)

        return () => clearTimeout(delayDebounceFn)
    }, [searchQuery])

    const isEdit = !!announcement

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: '',
            body: '',
            priority: 1,
            targetScope: isSuperAdmin ? 0 : 2,
            targetCompanyId: isSuperAdmin ? null : currentUser?.CompanyId,
            targetUserId: null,
            expire: null,
        },
    })

    const targetScope = watch('targetScope')
    const targetCompanyId = watch('targetCompanyId')

    useEffect(() => {
        if (announcement) {
            reset({
                title: announcement.Title || '',
                body: announcement.Body || '',
                priority: announcement.Priority || 1,
                targetScope: announcement.TargetScope || 0,
                targetCompanyId: announcement.TargetCompanyId || null,
                targetUserId: announcement.TargetUserId || null,
                expire: announcement.expire ? new Date(announcement.expire) : null,
            })
        } else {
            reset({
                title: '',
                body: '',
                priority: 1,
                targetScope: isSuperAdmin ? 0 : 2,
                targetCompanyId: isSuperAdmin ? null : currentUser?.CompanyId,
                targetUserId: null,
                expire: null,
            })
        }
    }, [announcement, open, isSuperAdmin, currentUser, reset])

    useEffect(() => {
        if (open && (targetScope === 2 || (targetScope === 1 && targetCompanyId))) {
            const fetchUsers = async () => {
                try {
                    const companyId = targetScope === 2 ? (targetCompanyId || currentUser?.CompanyId) : targetCompanyId
                    if (!companyId) return
                    const res = await UsersService.getTeamUsers({ CompanyId: companyId })
                    const items = Array.isArray(res) ? res : (res?.Items || [])
                    setUserOptions(items.map(u => ({
                        key: u.unique_id,
                        value: u.unique_id,
                        text: u.FullName || u.Email,
                    })))
                } catch (e) {
                    console.error(e)
                }
            }
            fetchUsers()
        }
    }, [open, targetScope, targetCompanyId, currentUser])

    const handleSave = async (formData) => {
        setLoading(true)
        try {
            const payload = {
                ...formData,
                targetCompanyId: formData.targetScope === 0 ? null : formData.targetCompanyId,
                targetUserId: formData.targetScope === 2 ? formData.targetUserId : null,
                imagePath: null,
                expire: formData.expire ? formData.expire.toISOString() : null,
            }

            if (isEdit) {
                await AnnouncementService.update(announcement.AnnouncementId, payload)
            } else {
                await AnnouncementService.create(payload)
            }
            onClose(true)
        } catch (e) {
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const scopeOptions = [
        { key: 0, value: 0, text: 'All Companies', disabled: !isSuperAdmin },
        { key: 1, value: 1, text: 'Specific Company', disabled: !isSuperAdmin },
        { key: 2, value: 2, text: 'Specific User' },
    ]

    const priorityOptions = [
        { key: 1, value: 1, text: 'Normal' },
        { key: 2, value: 2, text: 'Medium' },
        { key: 3, value: 3, text: 'High' },
    ]

    return (
        <Modal open={open} onClose={() => onClose(false)} size="small" scrolling>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2rem 1.5rem 0.5rem 1.5rem' }}>
                <div style={{ fontSize: 22, fontWeight: 500 }}>
                    {isEdit ? 'Edit Announcement' : 'Create Announcement'}
                </div>
                <button
                    type="button"
                    onClick={() => onClose(false)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 28, lineHeight: 1 }}
                    aria-label="Close"
                >
                    <X size={28} />
                </button>
            </div>

            <Modal.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Form style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <FormTextFormField
                        control={control}
                        name="title"
                        label="Title"
                        placeholder="Announcement Title"
                        errors={errors.title}
                        required
                    />
                    <FormTextAreaFormField
                        control={control}
                        name="body"
                        label="Body"
                        placeholder="Announcement Content"
                        errors={errors.body}
                        required
                    />
                    <Form.Group widths="equal">
                        <FormDropdownField
                            control={control}
                            name="priority"
                            label="Priority"
                            options={priorityOptions}
                            errors={errors.priority}
                        />
                        {!isEdit && (
                            <FormDropdownField
                                control={control}
                                name="targetScope"
                                label="Target Scope"
                                options={scopeOptions}
                                onChange={(_, data) => {
                                    setValue('targetScope', data.value)
                                    setValue('targetCompanyId', null)
                                    setValue('targetUserId', null)
                                }}
                                errors={errors.targetScope}
                            />
                        )}
                    </Form.Group>
                    <FormDateTimeField
                        control={control}
                        name="expire"
                        label="Expire Date & Time"
                        placeholder="Select expiration date and time"
                        errors={errors.expire}
                    />

                    {!isEdit && (
                        <>
                            {targetScope === 1 && isSuperAdmin && (
                                <FormDropdownField
                                    control={control}
                                    name="targetCompanyId"
                                    label="Select Company"
                                    options={companyOptions}
                                    placeholder="Select Company"
                                    errors={errors.targetCompanyId}
                                    required
                                    search
                                    onSearchChange={(e, data) => setSearchQuery(data.searchQuery)}
                                />
                            )}
                            {targetScope === 2 && (
                                <>
                                    {isSuperAdmin && (
                                        <FormDropdownField
                                            control={control}
                                            name="targetCompanyId"
                                            label="Select Company"
                                            options={companyOptions}
                                            placeholder="Select Company"
                                            onChange={(_, data) => {
                                                setValue('targetCompanyId', data.value)
                                                setValue('targetUserId', null)
                                            }}
                                            errors={errors.targetCompanyId}
                                            required
                                            search
                                            onSearchChange={(e, data) => setSearchQuery(data.searchQuery)}
                                        />
                                    )}
                                    <FormDropdownField
                                        control={control}
                                        name="targetUserId"
                                        label="Select User"
                                        options={userOptions}
                                        placeholder="Select User"
                                        errors={errors.targetUserId}
                                        disabled={isSuperAdmin && !targetCompanyId}
                                        required
                                        search
                                    />
                                </>
                            )}
                        </>
                    )}
                </Form>
            </Modal.Content>
            <Modal.Actions style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <AppSecondaryButton onClick={() => onClose(false)} disabled={loading}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton onClick={handleSubmit(handleSave)} loading={loading} disabled={loading}>
                    {isEdit ? 'Update' : 'Create'}
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}